import { useEffect, useRef, useState } from "react";
import { useProfileStore } from "@/store/profile";
import type { AuthInfo } from "@/store/profile";
import authRequest from "@/api/auth-request";
import { toast } from "sonner";
import { cn } from "@/utils/cn";
import { Card, CardHeader } from "@/components/ui/Card";

const formatPhoneDisplay = (v: string) => {
  const d = String(v).replace(/\D/g, "").slice(0, 10);
  if (d.length >= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length >= 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return d;
};
const phoneDigits = (v: string) => v.replace(/\D/g, "");

// Read-only fields: compact label/value rows with hairline dividers below sm (a long value wraps
// under its label); stacked label over value in the grid from sm.
const READONLY_ROW_CLASS =
  "flex min-h-11 min-w-0 flex-wrap items-center justify-between gap-x-4 border-b border-[var(--hs-line)] py-2 sm:block sm:min-h-0 sm:border-0 sm:py-0";
const READONLY_VALUE_CLASS =
  "ml-auto min-w-0 break-words text-right text-[15px] font-bold text-healthsource-dark sm:ml-0 sm:text-left";

export interface LoginInfoProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export function LoginInfo({ onDirtyChange }: LoginInfoProps) {
  const profileAuthInfo = useProfileStore((s) => s.profileAuthInfo);
  const setProfileAuthInfo = useProfileStore((s) => s.setProfileAuthInfo);

  const username = profileAuthInfo?.email ?? "";
  const userId = (profileAuthInfo as { userID?: string; id?: string })?.userID ?? (profileAuthInfo as { id?: string })?.id;

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phonenumber, setPhonenumber] = useState("");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const snapshotRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (userId ?? (profileAuthInfo as { id?: string })?.id) {
      setFirstname(profileAuthInfo?.firstName ?? "");
      setLastname(profileAuthInfo?.lastName ?? "");
      const mobile = (profileAuthInfo as { mobilePhone?: string })?.mobilePhone ?? "";
      setPhonenumber(formatPhoneDisplay(mobile));
    }
  }, [profileAuthInfo, userId]);

  const isDirty = editing && (
    firstname !== (snapshotRef.current.firstname ?? "") ||
    lastname !== (snapshotRef.current.lastname ?? "") ||
    phonenumber !== (snapshotRef.current.phonenumber ?? "")
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const startEditing = () => {
    snapshotRef.current = { firstname, lastname, phonenumber };
    setEditing(true);
  };

  const revert = () => {
    setFirstname(snapshotRef.current.firstname ?? "");
    setLastname(snapshotRef.current.lastname ?? "");
    setPhonenumber(snapshotRef.current.phonenumber ?? "");
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      setEditing(false);
    }
  };

  const confirmDiscard = () => {
    revert();
    setShowDiscardModal(false);
    setEditing(false);
  };

  const handleUpdate = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await authRequest.request.updateUser({
        id: String(userId),
        firstName: firstname,
        lastName: lastname,
        nickName: (profileAuthInfo as { nickName?: string })?.nickName ?? "",
      });
      // The phone travels in the URL path, so an empty value has no route to hit.
      const mobilePhone = phoneDigits(phonenumber);
      if (mobilePhone) {
        await authRequest.request.updateUserMobilephone({ id: String(userId), mobilePhone });
      }
      const res = await authRequest.request.getUser({ email: profileAuthInfo?.email ?? "" });
      setProfileAuthInfo((res.data ?? null) as AuthInfo | null);
      toast.success("Profile info updated");
      setEditing(false);
      snapshotRef.current = { firstname, lastname, phonenumber };
    } catch {
      toast.error("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  // --- Password state (separate section)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPwd, setUserPwd] = useState("");

  const checkPassword = (pwd: string) => {
    const len = pwd.length;
    const hasEight = len > 8;
    const hasNumber = /\d/.test(pwd);
    const hasUppercase = /[A-Z]/.test(pwd);
    const format = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    const hasSpecial = format.test(pwd);
    return { hasEight, hasNumber, hasUppercase, hasSpecial, valid: hasEight && hasNumber && hasUppercase && hasSpecial };
  };

  const pw = checkPassword(password);
  const matchConfirm = password === confirmPassword && !!confirmPassword;

  const updatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw.valid || !matchConfirm || !userId) return;
    try {
      const response = await authRequest.request.updateUserPwd({
        id: String(userId),
        pwd: password,
      });
      if (response.data) {
        toast.success("Password update successful");
        setPassword("");
        setConfirmPassword("");
      } else {
        toast.error("Password update failed");
      }
    } catch {
      toast.error("Password update failed");
    }
  };

  const openModal = () => {
    setUserEmail(profileAuthInfo?.email ?? "");
    setUserPwd("");
    setModalEmail(true);
  };

  const updateEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    try {
      await authRequest.request.updateUserEmail({
        id: String(userId),
        email: userEmail,
        pwd: userPwd,
      });
      const res = await authRequest.request.getUser({ email: userEmail });
      setProfileAuthInfo((res.data ?? null) as AuthInfo | null);
      setModalEmail(false);
      toast.success("User Email Updated");
    } catch {
      toast.error("Failed to update email.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Login Information card: username (read-only) + first, last, phone in a row; edit/cancel/update + discard modal */}
      {/* Below sm the card is flattened onto the page background so the fields run to the page gutter. */}
      <Card className="mb-3 max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:!shadow-none">
        <CardHeader
          className="py-1.5 max-sm:px-0"
          action={
            !editing ? (
              <button
                type="button"
                onClick={startEditing}
                className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-navy transition-colors hover:text-healthsource-red"
              >
                <i className="fas fa-pen" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-gray transition-colors hover:text-healthsource-red"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={saving}
                  className="flex min-h-11 items-center gap-1.5 rounded-md px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: "var(--hs-red)" }}
                >
                  {saving ? "Updating…" : "Update"}
                </button>
              </div>
            )
          }
        >
          Login Information
        </CardHeader>
        <div className="pb-5 sm:px-5">
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-3 lg:grid-cols-4", editing && "gap-y-3")}>
            <Field label="Username" value={username} />
            {!editing ? (
              <>
                <Field label="First name" value={firstname} />
                <Field label="Last name" value={lastname} />
                <Field label="Phone" value={phonenumber} />
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="login-firstname" className="mb-1 block text-[13px] font-semibold text-healthsource-gray">
                    First name
                  </label>
                  <input
                    type="text"
                    id="login-firstname"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                    className="w-full rounded-lg border border-healthsource-lightLavender min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none transition-colors focus:border-healthsource-navy"
                  />
                </div>
                <div>
                  <label htmlFor="login-lastname" className="mb-1 block text-[13px] font-semibold text-healthsource-gray">
                    Last name
                  </label>
                  <input
                    type="text"
                    id="login-lastname"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                    className="w-full rounded-lg border border-healthsource-lightLavender min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none transition-colors focus:border-healthsource-navy"
                  />
                </div>
                <div>
                  <label htmlFor="login-phone" className="mb-1 block text-[13px] font-semibold text-healthsource-gray">
                    Phone
                  </label>
                  <input
                    type="text"
                    id="login-phone"
                    value={phonenumber}
                    onChange={(e) => setPhonenumber(formatPhoneDisplay(phoneDigits(e.target.value)))}
                    className="w-full rounded-lg border border-healthsource-lightLavender min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none transition-colors focus:border-healthsource-navy"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Discard confirmation modal */}
      {showDiscardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">
              You have unsaved changes that will be lost if you leave. Discard changes?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDiscardModal(false)}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={confirmDiscard}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password section */}
      <div>
        <p className="mb-2 text-[13px] font-extrabold text-healthsource-dark">Password</p>
        <form onSubmit={updatePassword} className="space-y-4">
          <ul className="list-inside list-disc text-[13px] text-healthsource-gray">
            <li className={cn("mb-1", pw.hasEight && "text-green-600 line-through")}>8 Characters</li>
            <li className={cn("mb-1", pw.hasNumber && "text-green-600 line-through")}>Contains Number</li>
            <li className={cn("mb-1", pw.hasUppercase && "text-green-600 line-through")}>Contains Uppercase</li>
            <li className={cn("mb-1", pw.hasSpecial && "text-green-600 line-through")}>Contains Special Character</li>
            {confirmPassword && (
              <li className={cn("mb-1", matchConfirm && "text-green-600 line-through")}>Confirm Password Match</li>
            )}
          </ul>
          <div className="max-w-sm">
            <div className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender focus-within:border-healthsource-navy">
              <button
                type="button"
                className="flex items-center bg-healthsource-lightGray px-3 text-healthsource-gray"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={0}
              >
                <i className={cn("far", showPassword ? "fa-eye-slash" : "fa-eye")} />
              </button>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                className="flex-1 min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none"
                placeholder="Password"
                maxLength={50}
                minLength={8}
                required
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="max-w-sm">
            <div className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender focus-within:border-healthsource-navy">
              <button
                type="button"
                className="flex items-center bg-healthsource-lightGray px-3 text-healthsource-gray"
                onClick={() => setShowConfirmPassword((s) => !s)}
                tabIndex={0}
              >
                <i className={cn("far", showConfirmPassword ? "fa-eye-slash" : "fa-eye")} />
              </button>
              <input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type={showConfirmPassword ? "text" : "password"}
                className="flex-1 min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none"
                placeholder="Confirm Password"
                maxLength={50}
                minLength={8}
                required
                autoComplete="new-password"
              />
            </div>
          </div>
          <button
            type="submit"
            className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: "var(--hs-red)" }}
            disabled={!pw.valid || !matchConfirm}
          >
            UPDATE PASSWORD
          </button>
        </form>
      </div>

      {/* Update Email button */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={openModal}
          className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
        >
          UPDATE EMAIL
        </button>
      </div>

      {/* Update Email modal */}
      {modalEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <form onSubmit={updateEmailSubmit} className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h2 className="text-[15px] font-extrabold text-healthsource-dark mb-4">Update User Credentials</h2>
            <div className="space-y-3">
              <div>
                <label htmlFor="useremail" className="mb-1 block text-[13px] font-semibold text-healthsource-gray">
                  Email
                </label>
                <input
                  type="email"
                  maxLength={100}
                  id="useremail"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  required
                  className="w-full rounded-lg border border-healthsource-lightLavender min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none focus:border-healthsource-navy"
                />
              </div>
              <div>
                <label htmlFor="userpwd" className="mb-1 block text-[13px] font-semibold text-healthsource-gray">
                  Password
                </label>
                <input
                  type="password"
                  maxLength={100}
                  id="userpwd"
                  value={userPwd}
                  onChange={(e) => setUserPwd(e.target.value)}
                  required
                  className="w-full rounded-lg border border-healthsource-lightLavender min-h-11 px-3 py-2 text-base text-healthsource-dark md:text-[13px] outline-none focus:border-healthsource-navy"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
                onClick={() => setModalEmail(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className={READONLY_ROW_CLASS}>
      <p className="text-[13px] font-semibold text-healthsource-gray">{label}</p>
      <p className={READONLY_VALUE_CLASS}>{value || "—"}</p>
    </div>
  );
}
